// src/client/admin.ts — 住持後台：身份驗證、上傳曲目、列出/編輯/刪除
import { ZenAPI } from "./api";

interface AdminTrack {
  id: string;
  title: string;
  category: "focus" | "break";
  r2_key: string;
  is_published: number;
  source_prompt: string | null;
  content_type?: string;
  duration_seconds?: number | null;
  uploaded_by?: string | null;
  created_at?: number;
}

interface AdminUser {
  id: string;
  email: string;
  is_admin: number;
  created_at: number;
}

interface AdminStats {
  users: number;
  sessions: number;
  tracks: number;
  totalFocusMinutes: number;
  todaySessions: number;
  todayFocusMinutes: number;
}

interface RecentSession {
  id: string;
  user_id: string;
  email: string | null;
  started_at: number;
  completed_at: number | null;
  duration_minutes: number;
  type: string;
  task_label: string | null;
}

const $ = (id: string): HTMLElement | null => document.getElementById(id);

function showGate(msg: string): void {
  const g = $("gate"); if (g) g.style.display = "block";
  const a = $("admin"); if (a) a.style.display = "none";
  const gm = $("gateMsg"); if (gm && msg) gm.textContent = msg;
}
function showAdmin(): void {
  const g = $("gate"); if (g) g.style.display = "none";
  const a = $("admin"); if (a) a.style.display = "grid";
}
function msg(el: HTMLElement | null, type: "ok" | "err" | "" | undefined, text: string): void {
  if (!el) return;
  el.style.display = text ? "block" : "none";
  el.className = `msg ${type ?? ""}`;
  el.textContent = text || "";
}

async function loadTracks(): Promise<void> {
  const tbody = $("tracksBody");
  if (!tbody) return;
  try {
    const rows = await ZenAPI.get<AdminTrack[]>("/api/admin/tracks");
    tbody.replaceChildren();
    rows.forEach((r) => tbody.appendChild(renderRow(r)));
    msg($("tracksMsg"), "", "");
  } catch (e) {
    const err = e as Error;
    msg($("tracksMsg"), "err", `讀取曲目失敗：${err.message || err}`);
  }
}

function renderRow(r: AdminTrack): HTMLTableRowElement {
  const tr = document.createElement("tr");
  const isSynth = (r.r2_key || "").startsWith("synth://");
  tr.innerHTML = `
    <td class="title">${escapeHtml(r.title)}</td>
    <td><span class="pill ${r.category}">${r.category === "focus" ? "專注" : "休息"}</span></td>
    <td class="id">${escapeHtml(r.id)}</td>
    <td>${isSynth ? '<span class="pill synth">synth</span>' : `<code style="font-size:11.5px;">${escapeHtml(r.r2_key)}</code>`}</td>
    <td>${r.is_published ? "已發佈" : "草稿"}</td>
    <td class="prompt" style="max-width:200px; color:var(--ink-faint); font-size:11.5px;">${
      r.source_prompt ? escapeHtml(r.source_prompt.slice(0, 80)) + (r.source_prompt.length > 80 ? "…" : "") : "—"
    }</td>
    <td class="actions"></td>`;
  const actions = tr.querySelector(".actions") as HTMLElement;

  const toggle = document.createElement("button");
  toggle.className = "btn-ghost";
  toggle.style.fontSize = "12px";
  toggle.textContent = r.is_published ? "下架" : "發佈";
  toggle.addEventListener("click", async () => {
    try {
      await ZenAPI.put(`/api/admin/tracks/${encodeURIComponent(r.id)}`, { isPublished: !r.is_published });
      loadTracks();
    } catch (e) {
      const err = e as Error;
      alert(`失敗：${err.message || err}`);
    }
  });
  actions.appendChild(toggle);

  if (!isSynth) {
    const del = document.createElement("button");
    del.className = "btn-ghost";
    del.style.fontSize = "12px";
    del.style.color = "var(--cinnabar)";
    del.textContent = "刪除";
    del.addEventListener("click", async () => {
      if (!confirm(`確定要刪除「${r.title}」？R2 物件也會一併刪除。`)) return;
      try {
        await ZenAPI.delete(`/api/admin/tracks/${encodeURIComponent(r.id)}`);
        loadTracks();
      } catch (e) {
        const err = e as Error;
        alert(`失敗：${err.message || err}`);
      }
    });
    actions.appendChild(del);
  }
  return tr;
}

function escapeHtml(s: string | null | undefined): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
  );
}

async function loadStats(): Promise<void> {
  try {
    const stats = await ZenAPI.get<AdminStats>("/api/admin/stats");
    const set = (id: string, val: number) => {
      const el = $(id);
      if (el) el.textContent = String(val);
    };
    set("statUsers", stats.users);
    set("statSessions", stats.sessions);
    set("statTracks", stats.tracks);
    set("statFocusMin", stats.totalFocusMinutes);
    set("statTodaySessions", stats.todaySessions);
    set("statTodayMin", stats.todayFocusMinutes);
  } catch (e) {
    console.error("Failed to load stats:", e);
  }
}

async function loadUsers(): Promise<void> {
  const tbody = $("usersBody");
  if (!tbody) return;
  try {
    const users = await ZenAPI.get<AdminUser[]>("/api/admin/users");
    tbody.replaceChildren();
    users.forEach((u) => tbody.appendChild(renderUserRow(u)));
    msg($("usersMsg"), "", "");
  } catch (e) {
    const err = e as Error;
    msg($("usersMsg"), "err", `讀取用戶失敗：${err.message || err}`);
  }
}

function renderUserRow(u: AdminUser): HTMLTableRowElement {
  const tr = document.createElement("tr");
  const createdAt = new Date(u.created_at * 1000).toLocaleString("zh-TW");
  tr.innerHTML = `
    <td>${escapeHtml(u.email)}</td>
    <td>${u.is_admin ? '<span class="pill focus">住持</span>' : '<span class="pill break">用戶</span>'}</td>
    <td style="font-size:12px; color:var(--ink-soft);">${createdAt}</td>
    <td class="actions"></td>`;
  const actions = tr.querySelector(".actions") as HTMLElement;

  const toggle = document.createElement("button");
  toggle.className = "btn-ghost";
  toggle.style.fontSize = "12px";
  toggle.textContent = u.is_admin ? "撤銷住持" : "授予住持";
  toggle.addEventListener("click", async () => {
    const action = u.is_admin ? "撤銷" : "授予";
    if (!confirm(`確定要${action}「${u.email}」的住持權限？`)) return;
    try {
      await ZenAPI.put(`/api/admin/users/${encodeURIComponent(u.id)}`, { isAdmin: !u.is_admin });
      loadUsers();
      loadStats(); // 刷新統計
    } catch (e) {
      const err = e as Error;
      alert(`失敗：${err.message || err}`);
    }
  });
  actions.appendChild(toggle);
  return tr;
}

async function loadRecentSessions(): Promise<void> {
  const tbody = $("sessionsBody");
  if (!tbody) return;
  try {
    const sessions = await ZenAPI.get<RecentSession[]>("/api/admin/recent-sessions");
    tbody.replaceChildren();
    sessions.forEach((s) => tbody.appendChild(renderSessionRow(s)));
    msg($("sessionsMsg"), "", "");
  } catch (e) {
    const err = e as Error;
    msg($("sessionsMsg"), "err", `讀取會話失敗：${err.message || err}`);
  }
}

function renderSessionRow(s: RecentSession): HTMLTableRowElement {
  const tr = document.createElement("tr");
  const startedAt = new Date(s.started_at * 1000).toLocaleString("zh-TW");
  const completedAt = s.completed_at ? new Date(s.completed_at * 1000).toLocaleString("zh-TW") : "—";
  const typeLabel = s.type === "focus" ? "專注" : s.type === "short_break" ? "小憩" : "長憩";
  tr.innerHTML = `
    <td>${escapeHtml(s.email || "未知")}</td>
    <td><span class="pill ${s.type === "focus" ? "focus" : "break"}">${typeLabel}</span></td>
    <td>${s.duration_minutes}</td>
    <td style="font-size:12px; color:var(--ink-soft);">${startedAt}</td>
    <td style="font-size:12px; color:var(--ink-soft);">${completedAt}</td>`;
  return tr;
}

function bindUpload(): void {
  const form = $("uploadForm") as HTMLFormElement | null;
  if (!form) return;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    msg($("uploadMsg"), "", "");
    const id = ($("trackId") as HTMLInputElement | null)?.value.trim().toLowerCase() ?? "";
    const title = ($("trackTitle") as HTMLInputElement | null)?.value.trim() ?? "";
    const category = ($("trackCategory") as HTMLSelectElement | null)?.value ?? "focus";
    const file = ($("trackFile") as HTMLInputElement | null)?.files?.[0];
    const sourcePrompt = ($("trackPrompt") as HTMLTextAreaElement | null)?.value.trim() ?? "";
    if (!id || !title || !file) {
      msg($("uploadMsg"), "err", "請完整填寫 ID、標題、檔案");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      msg($("uploadMsg"), "err", "檔案大於 25MB");
      return;
    }
    const fd = new FormData();
    fd.append("id", id);
    fd.append("title", title);
    fd.append("category", category);
    fd.append("file", file);
    if (sourcePrompt) fd.append("sourcePrompt", sourcePrompt);

    const bar = $("uploadProgress");
    const barInner = bar?.querySelector("i") as HTMLElement | null;
    if (bar) bar.classList.add("show");
    if (barInner) barInner.style.width = "0%";
    try {
      await xhrUpload("/api/admin/tracks", fd, (pct) => {
        if (barInner) barInner.style.width = `${pct}%`;
      });
      msg($("uploadMsg"), "ok", "上傳成功 · 已存入 R2 與 D1");
      form.reset();
      if (bar) bar.classList.remove("show");
      loadTracks();
    } catch (e) {
      const err = e as Error;
      if (bar) bar.classList.remove("show");
      msg($("uploadMsg"), "err", `上傳失敗：${err.message || err}`);
    }
  });
}

async function csrfToken(): Promise<string | null> {
  const read = () => document.cookie.split(/;\s*/).find((p) => p.startsWith("bt_csrf="))?.slice("bt_csrf=".length) ?? null;
  const existing = read();
  if (existing) return existing;
  await fetch("/api/csrf", { credentials: "include" }).catch(() => null);
  return read();
}

async function xhrUpload(url: string, formData: FormData, onProgress: (pct: number) => void): Promise<unknown> {
  const csrf = await csrfToken();
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true;
    if (csrf) xhr.setRequestHeader("x-bt-csrf", csrf);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let data: { error?: string } | null = null;
      try { data = JSON.parse(xhr.responseText) as { error?: string }; } catch { data = null; }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new Error(data?.error || xhr.statusText || `HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("network error"));
    xhr.send(formData);
  });
}

async function main(): Promise<void> {
  try {
    const me = await ZenAPI.get<{ user: { isAdmin: boolean } | null }>("/api/me");
    if (!me?.user) { showGate("尚未登入。請回首頁點「登入」。"); return; }
    if (!me.user.isAdmin) { showGate("此頁面只有住持（admin）能進入。"); return; }
    showAdmin();
    bindUpload();
    loadStats();
    loadTracks();
    loadUsers();
    loadRecentSessions();
  } catch (e) {
    const err = e as Error;
    showGate(`無法驗證身份：${err.message || err}`);
  }
}

main();
